import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Search, Upload, Download, Users, Building, Armchair, 
  FileSpreadsheet, RefreshCw, AlertCircle, CheckCircle, XCircle, Plus, Trash2, Calendar
} from 'lucide-react';

export default function RamzanSeatPage() {
  const { api, user, isAdmin, isSuperAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [stats, setStats] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showCreateEventDialog, setShowCreateEventDialog] = useState(false);
  const [uploadMode, setUploadMode] = useState('update');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [newEventName, setNewEventName] = useState('');
  const [uploadEventName, setUploadEventName] = useState('');
  const [uploadEventId, setUploadEventId] = useState('');
  const [activeTab, setActiveTab] = useState('search');
  const [loading, setLoading] = useState(true);

  // Check if user is seat_management role (search only)
  const isSeatManagement = user?.role === 'seat_management';

  // Fetch events on mount
  useEffect(() => {
    fetchEvents();
  }, []);

  // Fetch stats when event changes
  useEffect(() => {
    if (selectedEvent) {
      fetchStats(selectedEvent.id);
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/ramzan-seat-events');
      const eventsList = response.data.events || [];
      setEvents(eventsList);
      if (eventsList.length > 0 && !selectedEvent) {
        setSelectedEvent(eventsList[0]);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (eventId) => {
    try {
      const response = await api.get(`/ramzan-seats/stats?event_id=${eventId}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEventName.trim()) {
      toast.error('Please enter event name');
      return;
    }
    try {
      const response = await api.post('/ramzan-seat-events', { name: newEventName });
      toast.success('Event created');
      setShowCreateEventDialog(false);
      setNewEventName('');
      fetchEvents();
      if (response.data.event) {
        setSelectedEvent(response.data.event);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event and all its seats?')) return;
    try {
      await api.delete(`/ramzan-seat-events/${eventId}`);
      toast.success('Event deleted');
      fetchEvents();
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(null);
        setStats(null);
      }
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // Real-time search
  const handleSearch = useCallback(async (query) => {
    if (query.length < 1) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      let url = `/ramzan-seats/search?q=${encodeURIComponent(query)}`;
      if (selectedEvent) {
        url += `&event_id=${selectedEvent.id}`;
      }
      const response = await api.get(url);
      setSearchResults(response.data.seats || []);
    } catch (error) {
      toast.error('Search failed');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [api, selectedEvent]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        handleSearch(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const handleFileUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    if (!uploadEventId && !uploadEventName.trim()) {
      toast.error('Please select existing event or enter new event name');
      return;
    }

    setUploading(true);
    setUploadErrors([]);

    const formData = new FormData();
    formData.append('file', uploadFile);

    let url = `/ramzan-seats/upload?mode=${uploadMode}`;
    if (uploadEventId) {
      url += `&event_id=${uploadEventId}`;
    } else {
      url += `&event_name=${encodeURIComponent(uploadEventName)}`;
    }

    try {
      const response = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setShowUploadDialog(false);
        setUploadFile(null);
        setUploadEventName('');
        setUploadEventId('');
        fetchEvents();
        if (response.data.event_id && selectedEvent?.id === response.data.event_id) {
          fetchStats(response.data.event_id);
        }
      } else {
        setUploadErrors(response.data.errors || []);
        toast.error(`Upload failed with ${response.data.error_count} errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async (filterType, floor = null) => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }
    try {
      let url = `/ramzan-seats/export?format=pdf&filter_type=${filterType}&event_id=${selectedEvent.id}`;
      if (floor) url += `&floor=${floor}`;
      
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${selectedEvent.name}_${filterType}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleExcelExport = async (filterType) => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }
    try {
      const response = await api.get(`/ramzan-seats/export?format=excel&filter_type=${filterType}&event_id=${selectedEvent.id}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${selectedEvent.name}_${filterType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Excel downloaded');
    } catch (error) {
      toast.error('Excel export failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  // Seat Management role - Search only view
  if (isSeatManagement) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-navy flex items-center gap-2">
              <Armchair className="w-6 h-6 sm:w-7 sm:h-7 text-gold" />
              Ramzan Seat Search
            </h1>
            <p className="text-slate-600 text-xs sm:text-sm mt-1">Search seat allocations</p>
          </div>
        </div>

        {/* Event Selector for seat_management */}
        {events.length > 0 && (
          <Card className="border-0 shadow-card">
            <CardContent className="p-3 sm:pt-4 sm:pb-4">
              <Label className="text-xs sm:text-sm mb-2 block">Select Event</Label>
              <Select value={selectedEvent?.id || ''} onValueChange={(val) => setSelectedEvent(events.find(e => e.id === val))}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name} ({event.total_seats} seats)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}

        {/* Search Card */}
        <Card className="border-0 shadow-card">
          <CardContent className="p-3 sm:pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
              <Input
                placeholder="Search ITS, Name, or Seat..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 sm:pl-10 h-10 sm:h-12 text-base sm:text-lg"
                data-testid="seat-search-input"
              />
              {searching && (
                <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 animate-spin" />
              )}
            </div>

            {/* Search Results - Mobile Card View */}
            {searchResults.length > 0 && (
              <div className="mt-4 sm:mt-6 space-y-3">
                {/* Mobile Card View */}
                <div className="block sm:hidden space-y-3">
                  {searchResults.map((seat, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-3 border" data-testid={`seat-result-${seat.its_id}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{seat.full_name}</p>
                          <p className="text-xs text-slate-500 font-mono mt-1">ITS: {seat.its_id}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-navy">{seat.seat_number}</p>
                          <Badge variant="outline" className="text-xs mt-1">{seat.floor}</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Desktop Table View */}
                <div className="hidden sm:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ITS Number</TableHead>
                        <TableHead>Full Name</TableHead>
                        <TableHead>Floor</TableHead>
                        <TableHead>Seat Number</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {searchResults.map((seat, idx) => (
                        <TableRow key={idx} data-testid={`seat-result-desktop-${seat.its_id}`}>
                          <TableCell className="font-mono">{seat.its_id}</TableCell>
                          <TableCell className="font-medium">{seat.full_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{seat.floor}</Badge>
                          </TableCell>
                          <TableCell className="font-bold text-lg text-navy">{seat.seat_number}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {searchQuery.length >= 1 && searchResults.length === 0 && !searching && (
              <div className="text-center py-6 sm:py-8 text-slate-500">
                <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-slate-300 mb-2" />
                <p className="text-sm sm:text-base">No seats found for "{searchQuery}"</p>
              </div>
            )}

            {searchQuery.length < 1 && (
              <div className="text-center py-8 sm:py-12 text-slate-400">
                <Search className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-slate-200 mb-3 sm:mb-4" />
                <p className="text-base sm:text-lg">Enter ITS, Name, or Seat No</p>
                <p className="text-xs sm:text-sm mt-1">Minimum 1 character required</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin view with full functionality
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-navy flex items-center gap-2">
            <Armchair className="w-6 h-6 sm:w-7 sm:h-7 text-gold" />
            Ramzan Seats
          </h1>
          <p className="text-slate-600 text-xs sm:text-sm mt-1 hidden sm:block">Manage seat allocations</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={() => setShowCreateEventDialog(true)} size="sm" className="flex-1 sm:flex-none">
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">New Event</span>
          </Button>
          <Button onClick={() => setShowUploadDialog(true)} className="bg-navy hover:bg-navy-light flex-1 sm:flex-none" size="sm">
            <Upload className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Upload</span>
          </Button>
        </div>
      </div>

      {/* Event Selector */}
      <Card className="border-0 shadow-card">
        <CardContent className="p-3 sm:pt-4 sm:pb-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
            <div className="flex-1 min-w-0 sm:min-w-[200px]">
              <Label className="text-xs sm:text-sm mb-2 block">Select Event</Label>
              <Select value={selectedEvent?.id || ''} onValueChange={(val) => setSelectedEvent(events.find(e => e.id === val))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {event.name} ({event.total_seats})
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedEvent && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-red-500 hover:text-red-700 hover:bg-red-50 w-full sm:w-auto"
                onClick={() => handleDeleteEvent(selectedEvent.id)}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete Event
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {stats && selectedEvent && (
        <div className="grid grid-cols-2 gap-2 sm:gap-4 md:grid-cols-4">
          <Card className="border-0 shadow-card">
            <CardContent className="p-2 sm:pt-4 sm:pb-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold text-navy">{stats.total}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card">
            <CardContent className="p-2 sm:pt-4 sm:pb-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold text-emerald-600">{stats.admin_booked}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Admin</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card">
            <CardContent className="p-2 sm:pt-4 sm:pb-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold text-purple-600">{stats.user_booked}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">User</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-card">
            <CardContent className="p-2 sm:pt-4 sm:pb-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-amber-100 rounded-lg">
                  <Building className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold text-amber-600">{stats.floors?.length || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Floors</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {!selectedEvent && events.length === 0 && (
        <Card className="border-0 shadow-card">
          <CardContent className="py-12 text-center">
            <Armchair className="w-16 h-16 mx-auto text-slate-200 mb-4" />
            <p className="text-slate-500 mb-4">No events created yet</p>
            <Button onClick={() => setShowCreateEventDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create First Event
            </Button>
          </CardContent>
        </Card>
      )}

      {selectedEvent && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex-wrap">
            <TabsTrigger value="search" className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              Search
            </TabsTrigger>
            <TabsTrigger value="unbooked" className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Not Booked
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Reports
            </TabsTrigger>
          </TabsList>

          {/* Search Tab */}
          <TabsContent value="search" className="mt-4">
            <Card className="border-0 shadow-card">
              <CardContent className="pt-6">
                <div className="relative max-w-xl">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    placeholder="Search by ITS, Name, or Seat No..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="admin-seat-search-input"
                  />
                  {searching && (
                    <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 animate-spin" />
                  )}
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="mt-6 overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>ITS Number</TableHead>
                          <TableHead>Full Name</TableHead>
                          <TableHead>Floor</TableHead>
                          <TableHead>Seat Number</TableHead>
                          <TableHead>Admin Booked</TableHead>
                          <TableHead>User Booked</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {searchResults.map((seat, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono">{seat.its_id}</TableCell>
                            <TableCell className="font-medium">{seat.full_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{seat.floor}</Badge>
                            </TableCell>
                            <TableCell className="font-bold text-navy">{seat.seat_number}</TableCell>
                            <TableCell>
                              {seat.admin_booked ? (
                                <Badge className="bg-emerald-100 text-emerald-700">Yes</Badge>
                              ) : (
                                <Badge variant="outline">No</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {seat.user_booked ? (
                                <Badge className="bg-purple-100 text-purple-700">Yes</Badge>
                              ) : (
                                <Badge variant="outline">No</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {searchQuery.length >= 1 && searchResults.length === 0 && !searching && (
                  <div className="text-center py-8 text-slate-500">
                    <AlertCircle className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p>No seats found for "{searchQuery}"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Unbooked Eligible Members Tab */}
          <TabsContent value="unbooked" className="mt-4">
            <UnbookedMembersDashboard api={api} eventId={selectedEvent?.id} />
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Export Options */}
              <Card className="border-0 shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Download className="w-5 h-5 text-navy" />
                    Export Reports
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start" 
                    onClick={() => handleExport('all')}
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Full Seat Allocation List (PDF)
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start" 
                    onClick={() => handleExport('admin_booked')}
                  >
                    <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
                    Admin Booked List (PDF)
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start" 
                    onClick={() => handleExport('user_booked')}
                  >
                    <CheckCircle className="w-4 h-4 mr-2 text-purple-600" />
                    User Booked List (PDF)
                  </Button>
                  {isSuperAdmin && (
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-emerald-700 border-emerald-200 hover:bg-emerald-50" 
                      onClick={() => handleExcelExport('all')}
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Full List (Excel)
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Floor-wise Export */}
              <Card className="border-0 shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building className="w-5 h-5 text-navy" />
                    Floor-wise Reports
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {stats?.floors?.map((floor) => (
                    <Button 
                      key={floor.floor}
                      variant="outline" 
                      className="w-full justify-between" 
                      onClick={() => handleExport('floor', floor.floor)}
                    >
                      <span className="flex items-center">
                        <Building className="w-4 h-4 mr-2" />
                        Floor {floor.floor}
                      </span>
                      <Badge variant="secondary">{floor.count} seats</Badge>
                    </Button>
                  ))}
                  {(!stats?.floors || stats.floors.length === 0) && (
                    <p className="text-center text-slate-500 py-4">No floor data available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Create Event Dialog */}
      <Dialog open={showCreateEventDialog} onOpenChange={setShowCreateEventDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-navy" />
              Create New Event
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Event Name *</Label>
              <Input
                placeholder="e.g., Ramzan 1447H"
                value={newEventName}
                onChange={(e) => setNewEventName(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleCreateEvent} className="bg-navy hover:bg-navy-light">
              Create Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-navy" />
              Upload Seat Allocations
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg text-sm">
              <p className="font-medium text-blue-800 mb-2">Required Excel/CSV Columns:</p>
              <ul className="text-blue-700 space-y-1 list-disc list-inside">
                <li>ITS_Number</li>
                <li>Full_Name</li>
                <li>Floor</li>
                <li>Seat_Number</li>
                <li>Admin_Booked (Yes/No) - Optional</li>
                <li>User_Booked (Yes/No) - Optional</li>
              </ul>
              <p className="mt-3 text-blue-600 text-xs">
                Note: Only Misaq Done Ladies will be accepted
              </p>
            </div>

            <div>
              <Label>Event *</Label>
              <div className="space-y-2 mt-1">
                <Select value={uploadEventId} onValueChange={(val) => { setUploadEventId(val === 'new' ? '' : val); setUploadEventName(''); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select existing event or create new" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">-- Create New Event --</SelectItem>
                    {events.map((event) => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(!uploadEventId || uploadEventId === '') && (
                  <Input
                    placeholder="Enter new event name"
                    value={uploadEventName}
                    onChange={(e) => setUploadEventName(e.target.value)}
                  />
                )}
              </div>
            </div>

            <div>
              <Label>Upload Mode</Label>
              <Select value={uploadMode} onValueChange={setUploadMode}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="update">Update Existing (Add/Update)</SelectItem>
                  <SelectItem value="replace">Replace All Data</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {uploadMode === 'update' 
                  ? 'Will add new seats and update existing ones by ITS'
                  : 'Will delete all existing data for this event and replace'}
              </p>
            </div>

            <div>
              <Label>Select File (.xlsx, .xls, .csv)</Label>
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="mt-1"
              />
            </div>

            {/* Upload Errors */}
            {uploadErrors.length > 0 && (
              <div className="bg-red-50 p-4 rounded-lg max-h-48 overflow-y-auto">
                <p className="font-medium text-red-800 mb-2 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Validation Errors ({uploadErrors.length})
                </p>
                <ul className="text-red-700 text-sm space-y-1">
                  {uploadErrors.map((error, idx) => (
                    <li key={idx}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleFileUpload} 
              disabled={uploading || !uploadFile}
              className="bg-navy hover:bg-navy-light"
            >
              {uploading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Unbooked Members Dashboard Component
function UnbookedMembersDashboard({ api, eventId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total_eligible: 0, total_booked: 0 });
  const limit = 50;

  const fetchUnbooked = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const response = await api.get(`/ramzan-seats/unbooked-eligible`, {
        params: { event_id: eventId, page, limit, search }
      });
      setMembers(response.data.members || []);
      setTotal(response.data.total || 0);
      setStats({
        total_eligible: response.data.total_eligible || 0,
        total_booked: response.data.total_booked || 0
      });
    } catch (error) {
      toast.error('Failed to fetch unbooked members');
    } finally {
      setLoading(false);
    }
  }, [api, eventId, page, search]);

  useEffect(() => {
    fetchUnbooked();
  }, [fetchUnbooked]);

  const totalPages = Math.ceil(total / limit);

  return (
    <Card className="border-0 shadow-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-orange-500" />
          Eligible Females Not Booked
        </CardTitle>
        <p className="text-sm text-slate-500">
          Females with Misaq Done who haven't booked any seat yet
        </p>
      </CardHeader>
      <CardContent>
        {/* Stats Summary */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.total_eligible}</p>
            <p className="text-xs text-blue-700">Total Eligible</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.total_booked}</p>
            <p className="text-xs text-green-700">Booked</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-orange-600">{total}</p>
            <p className="text-xs text-orange-700">Not Booked</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by ITS or Name..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-10"
          />
        </div>

        {/* Members Table */}
        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-2" />
            <p>All eligible females have booked seats!</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[50px]">Sr</TableHead>
                    <TableHead>ITS Number</TableHead>
                    <TableHead>Full Name</TableHead>
                    <TableHead>HOF Name</TableHead>
                    <TableHead>Mohalla</TableHead>
                    <TableHead>Mobile</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member, idx) => (
                    <TableRow key={member.id || idx}>
                      <TableCell className="text-xs text-slate-500">{(page - 1) * limit + idx + 1}</TableCell>
                      <TableCell className="font-mono text-sm">{member.its_id || '-'}</TableCell>
                      <TableCell className="font-medium">{member.full_name || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{member.hof_name || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{member.mohalla || '-'}</TableCell>
                      <TableCell className="text-sm">{member.mobile || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-slate-500">
                  Showing {(page - 1) * limit + 1} - {Math.min(page * limit, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={page === totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
